import React, { memo } from 'react';
import PropTypes from 'prop-types';

import TablePagination from '@mui/material/TablePagination';

import IndicatorCell from '../shared/table/IndicatorCell';
import DateCell from '../shared/table/DateCell';

import { caseConstants } from '../../constants';
import { utils } from '../../util';

const {
  numberOfAClSteps,
  numberOfSteps,
  numberOfCmfExtendedSteps,
  numberOfCmfSteps,
  numberOfCustomSteps,
  statuses,
  caseActions,
  caseAclActions,
  caseCmfActions,
  caseCmfExtendedActions,
  procedureFlows,
  cmfExtendedProcedureFlows,
} = caseConstants;

const getCompanyLogoUrl = (logo) => (
  typeof logo === 'string' ? logo : logo?.downloadUrl || ''
);

const CasesTableMobile = ({ cases, onClick, withoutPagination, companies = [] }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getNumberFlowSteps = (flow) => {
    switch (flow) {
      case procedureFlows.CMF:
        return numberOfCmfSteps;
      case procedureFlows.ACL:
        return numberOfAClSteps;
      case procedureFlows.CUSTOM:
        return numberOfCustomSteps;
      default:
        if (cmfExtendedProcedureFlows.includes(flow)) {
          return numberOfCmfExtendedSteps;
        }
        return numberOfSteps;
    }
  };

  const getFlowActions = (flow) => {
    switch (flow) {
      case procedureFlows.CMF:
        return caseCmfActions;
      case procedureFlows.ACL:
        return caseAclActions;
      default:
        if (cmfExtendedProcedureFlows.includes(flow)) {
          return caseCmfExtendedActions;
        }
        return caseActions;
    }
  };

  return (
    <div className="cases-table-mobile" style={{ marginBottom: !withoutPagination ? 64 : 0 }}>
      {cases
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((item) => {
          const procedureFlow = item && item.procedureFlow;
          const numberFlowSteps = getNumberFlowSteps(procedureFlow);
          const actions = getFlowActions(procedureFlow);
          const action = procedureFlow === procedureFlows.ACL ? actions[item.step] : actions[item.step];
          const getFlowTheme = utils.getFlowsTheme(item?.procedureFlow, item.procedure).main;
          const company = companies.find((companyItem) => companyItem.id === item.company);
          const companyName = company?.name || item.companyName || '';
          const companyColor = company?.color || 'var(--color-text-secondary)';
          const companyLogoUrl = getCompanyLogoUrl(company?.logo);

          return (
            <div key={item.id} className="case-mobile-row" onClick={() => onClick(item)}>
              <div className="d-flex space-between">
                <div className="d-flex m-r-md flex-1">
                  <IndicatorCell
                    value={item.access}
                    complete={item.step > numberFlowSteps}
                    rejected={item.status === statuses.rejected}
                    archived={!!item.archived}
                    postponed={!!item.postponed}
                    expiry={item?.createdAt ? item?.createdAt : false}
                    style={getFlowTheme}
                  />
                  <div className="m-l-md">{item?.formattedId || item.id}</div>
                </div>
              {!!companyName && (
                <div className="m-t-sm d-flex" style={{ alignItems: 'center' }}>
                  {!!companyLogoUrl && (
                    <img
                      src={companyLogoUrl}
                      alt={`${companyName}-company-logo`}
                      style={{
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        objectFit: 'contain',
                        borderRadius: 4,
                      }}
                    />
                  )}
                  {!companyLogoUrl && (
                    <div style={{ color: companyColor, fontWeight: 600 }}>
                      {companyName}
                    </div>
                  )}
                </div>
              )}
              </div>
              <div className="m-t-sm d-flex space-between">
                <div className="m-r-sm flex-1">
                  {item.hospital}
                </div>
                <div className="d-flex">
                  <DateCell date={item.date} />
                </div>
              </div>

              <div className="m-t-sm">
                {item.procedureName || item.procedure || 'No procedure'}
              </div>
              <div className="d-flex space-between m-t-sm">
                <div style={{ color: getFlowTheme }}
                     className={`m-r-md flex-1 ${item.access ? 'primary' : 'secondary'}`}>
                  {action || ''}
                </div>
                <div className="d-flex flex-end">
                  <span
                    style={{ color: getFlowTheme }}>{item.step > numberFlowSteps ? numberFlowSteps : item.step}</span>
                  /{numberFlowSteps}
                </div>
              </div>
            </div>
          );
        })}
      {!withoutPagination && (
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={cases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </div>
  );
};

CasesTableMobile.propTypes = {
  cases: PropTypes.array,
  onClick: PropTypes.func,
  companies: PropTypes.array,
};

export default memo(CasesTableMobile);
